import { PERMISSIONS } from '@/lib/permissions'

export { CustomerSavingsPage } from './customer-savings'
export { SavingsAccountDetailPage } from './savings-account-detail'
export { SavingsAccountsPage } from './savings-accounts'
export { SavingsCashPage } from './savings-cash'
export { SavingsProductsPage } from './savings-products'
export { SavingsPeriodEndRunsPage } from './savings-runs'

export {
  DEFAULT_PAGE_SIZE as SAVINGS_DEFAULT_PAGE_SIZE,
  SAVINGS_ACCOUNT_TABS,
  SAVINGS_CASH_TABS,
  SAVINGS_CUSTOMER_TABS,
  savingsAccountDetailSearchSchema,
  savingsAccountListSearchSchema,
  savingsCashSearchSchema,
  savingsCustomerSearchSchema,
  savingsProductListSearchSchema,
  type SavingsAccountDetailSearch,
  type SavingsAccountListSearch,
  type SavingsAccountTab,
  type SavingsCashTab,
  type SavingsCustomerTab,
  type SavingsProductListSearch,
} from './search'

// The landing screen for `/savings` was a placeholder until now. Keeping the old name pointing at
// the account list means the route already registered as `/savings` goes on compiling and now
// resolves to a real screen, rather than the tree needing to change in the same commit as this
// area. `SavingsAccountsPage` is the name to use in new code.
export { SavingsAccountsPage as SavingsPage } from './savings-accounts'

/**
 * The routes this area owns, for the integrator to register.
 *
 * `path` is the URL, `componentName` the named export above, and `permission` the code the route
 * should be gated on — always the *loosest* one that could reach the screen, because every action
 * inside is gated again on its own code. `/savings/accounts/$accountId/cash` is the case that
 * makes the distinction matter: it carries four forms behind four different permissions, and
 * gating the route on any one of them would lock out a teller who holds one of the other three.
 * The screen renders only the tabs the role can actually post through, and says so plainly when
 * that is none of them.
 *
 * Each entry also records what the screen does on a phone, against `docs/mobile-policy.md`:
 * `adapts` means it stacks and stays usable, `refuses` means it shows the wider-display panel
 * below `lg` instead of degrading.
 *
 * The search schemas above belong to these routes — attach `savingsAccountListSearchSchema` to
 * `/savings`, `savingsAccountDetailSearchSchema` to the account, `savingsCashSearchSchema` to the
 * cash screen, `savingsCustomerSearchSchema` to the customer screen, and
 * `savingsProductListSearchSchema` to the products screen. Every screen also parses through its own
 * schema, so an unregistered or hand-edited search string degrades to the defaults rather than
 * throwing anyone out.
 */
export interface SavingsRouteDescriptor {
  path: string
  componentName: string
  permission: string
  phoneBehaviour: 'adapts' | 'refuses'
}

export const savingsRoutes: SavingsRouteDescriptor[] = [
  {
    path: '/savings',
    componentName: 'SavingsAccountsPage',
    permission: PERMISSIONS.savings.view,
    phoneBehaviour: 'adapts',
  },
  {
    path: '/savings/accounts/$accountId',
    componentName: 'SavingsAccountDetailPage',
    permission: PERMISSIONS.savings.view,
    phoneBehaviour: 'adapts',
  },
  {
    path: '/savings/accounts/$accountId/cash',
    componentName: 'SavingsCashPage',
    permission: PERMISSIONS.savings.view,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/savings/customers/$customerId',
    componentName: 'CustomerSavingsPage',
    permission: PERMISSIONS.savings.view,
    phoneBehaviour: 'adapts',
  },
  {
    path: '/savings/products',
    componentName: 'SavingsProductsPage',
    permission: PERMISSIONS.savings.productView,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/savings/runs',
    componentName: 'SavingsPeriodEndRunsPage',
    permission: PERMISSIONS.savings.interestPost,
    phoneBehaviour: 'refuses',
  },
]
